import { useState, useCallback, useEffect } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../api';

interface UseConvexInfiniteAppsProps {
  searchTerm?: string;
  pageSize?: number;
}

export const useConvexInfiniteApps = ({ 
  searchTerm, 
  pageSize = 50 
}: UseConvexInfiniteAppsProps) => {
  const [allApps, setAllApps] = useState<any[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Current query based on cursor
  const currentQuery = useQuery(api.apps.list.all, {
    query: searchTerm || undefined,
    paginationOpts: {
      cursor: cursor,
      numItems: pageSize
    }
  });

  // Reset when search term changes
  useEffect(() => {
    setAllApps([]);
    setCursor(null);
    setHasMore(true);
    setIsLoadingMore(false);
  }, [searchTerm]);

  // Handle new data from current query
  useEffect(() => {
    if (currentQuery?.page) {
      if (cursor === null) {
        // Initial load
        setAllApps(currentQuery.page);
      } else {
        // Append new data
        setAllApps(prev => [...prev, ...currentQuery.page]);
        setIsLoadingMore(false);
      }
      
      setHasMore(!currentQuery.isDone);
    }
  }, [currentQuery, cursor]);

  const loadMore = useCallback(() => {
    if (!isLoadingMore && hasMore && currentQuery?.continueCursor) {
      setIsLoadingMore(true);
      setCursor(currentQuery.continueCursor);
    }
  }, [isLoadingMore, hasMore, currentQuery?.continueCursor]);

  const isLoading = currentQuery === undefined && cursor === null;

  return {
    apps: allApps,
    isLoading,
    isLoadingMore,
    hasMore,
    loadMore
  };
};